import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

// Bootstrap Modules
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ButtonsModule } from 'ngx-bootstrap/buttons';

// Custom Modules & Services
import { BlockService, PageModule, SpinnerModule, UtilsModule } from '@gallaghercp/core-frontend';
import { TranslateModule } from '@ngx-translate/core';
import { SwiperModule } from '@gallaghercp/core-swiper';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

// Material Modules
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

// App Components
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { LogoComponent } from './logo/logo.component';
import { PageHeaderComponent } from './page-header/page-header.component';
import { PageFooterComponent } from './page-footer/page-footer.component';
import { MainComponent } from './main/main.component';

// Custom Blocks
import { BlockAnchorLinkComponent } from '../blocks/block-anchor-link/block-anchor-link.component';
import { BlockBenefitsMenuComponent } from '../blocks/block-benefits-menu/block-benefits-menu.component';
import { BlockBenefitsNewComponent } from '../blocks/block-benefits-new/block-benefits-new.component';
import { BlockBlockquoteComponent } from '../blocks/block-blockquote/block-blockquote.component';
import { BlockBreadcrumbsComponent } from '../blocks/block-breadcrumbs/block-breadcrumbs.component';
import { BlockBulletedListComponent } from '../blocks/block-bulleted-list/block-bulleted-list.component';
import { BlockButtonComponent } from '../blocks/block-button/block-button.component';
import { BlockCountdownComponent } from '../blocks/block-countdown/block-countdown.component';
import { BlockRelatedBenefitsComponent } from '../blocks/block-related-benefits/block-related-benefits.component';
import { BlockCategoryListComponent } from '../blocks/block-category-list/block-category-list.component';
import { BlockDownloadListComponent } from '../blocks/block-download-list/block-download-list.component';
import { BlockHeroComponent } from '../blocks/block-hero/block-hero.component';
import { BlockImageTextComponent } from '../blocks/block-image-text/block-image-text.component';
import { BlockTextComponent } from '../blocks/block-text/block-text.component';
import { BlockTextEditorComponent } from '../blocks/block-text-editor/block-text-editor.component';
import { BlockHeader1Component } from '../blocks/block-header1/block-header1.component';
import { BlockHeader2Component } from '../blocks/block-header2/block-header2.component';
import { BlockHeader3Component } from '../blocks/block-header3/block-header3.component';
import { BlockHeader4Component } from '../blocks/block-header4/block-header4.component';
import { BlockHeader5Component } from '../blocks/block-header5/block-header5.component';
import { BlockNumberedListComponent } from '../blocks/block-numbered-list/block-numbered-list.component';
import { BlockBackHomeComponent } from '../blocks/block-backhome/block-backhome.component';
import { BlockTabsComponent } from '../blocks/block-tabs/block-tabs.component';
import { BlockHTMLComponent } from '../blocks/block-html/block-html.component';
import { BlockFAQComponent } from '../blocks/block-faq/block-faq.component';
import { BlockCarouselStoriesComponent } from '../blocks/block-carousel-stories/block-carousel-stories.component';
import { BlockCardsComponent } from '../blocks/block-cards/block-cards.component';
import { BlockFeedbackComponent } from '../blocks/block-feedback/block-feedback.component';
import { BlockSitemapComponent } from '../blocks/block-sitemap/block-sitemap.component';
import { BlockParagraphLeadComponent } from '../blocks/block-paragraph-lead/block-paragraph-lead.component';
import { BlockParagraphComponent } from '../blocks/block-paragraph/block-paragraph.component';
import { BlockPersonaComponent } from '../blocks/block-persona/block-persona.component';
import { BlockDividerComponent } from '../blocks/block-divider/block-divider.component';
import { BlockFlexHeroComponent } from '../blocks/block-flex-hero/block-flex-hero.component';
import { BlockFlexContentComponent } from '../blocks/block-flex-content/block-flex-content.component';
import { BlockFlexStepsComponent } from '../blocks/block-flex-steps/block-flex-steps.component';
import { BlockFlexCardsComponent } from '../blocks/block-flex-cards/block-flex-cards.component';
import { BlockFlexCarouselComponent } from '../blocks/block-flex-carousel/block-flex-carousel.component';
import { BlockFAQsComponent } from '../blocks/block-flex-faqs/block-flex-faqs.component';
import { BlockDownloadAppComponent } from '../blocks/block-download-app/block-download-app.component';
import { BlockVideoCustomComponent } from '../blocks/block-video-custom/block-video-custom.component';
import { BlockHomeTilesComponent } from '../blocks/block-home-tiles/block-home-tiles.component';
import { BlockTileComponent } from '../blocks/block-tile/block-tile.component';
import { BlockToggleComponent } from '../blocks/block-toggle/block-toggle.component';
import { BlockHomeTileComponent } from '../blocks/block-home-tile/block-home-tile.component';
import { BlockFlexReasontilesComponent } from '../blocks/block-flex-reasontiles/block-flex-reasontiles.component';
import { BlockCardTilesComponent } from '../blocks/block-card-tiles/block-card-tiles.component';
import { BlockRelatedPagesComponent } from '../blocks/block-related-pages/block-related-pages.component';
import { BlockStoriesHeaderComponent } from '../blocks/block-stories-header/block-stories-header.component';
import { BlockHeadlinesIndexBannerComponent } from '../blocks/block-headlines-index-banner/block-headlines-index-banner.component';
import { BlockPanelsComponent } from '../blocks/block-panels/block-panels.component';
import { BlockStoriesIndexBannerComponent } from '../blocks/block-stories-index-banner/block-stories-index-banner.component';
import { BlockProviderLoginComponent } from '../blocks/block-provider-login/block-provider-login.component';
import { BlockDownloadComponent } from '../blocks/block-download/block-download.component';
import { BlockCloudVideoCtaComponent } from '../blocks/block-cloud-video-cta/block-cloud-video-cta.component';
import { BlockCloudReasontilesComponent } from '../blocks/block-cloud-reasontiles/block-cloud-reasontiles.component';
import { BlockCloudTileBanner } from '../blocks/block-cloud-tilebanner/block-cloud-tilebanner.component';
import { BlockCloudCTABannerComponent } from '../blocks/block-cloud-cta-banner/block-cloud-cta-banner.component';
import { BlockClientLanguageComponent } from '@core-app/blocks/block-client-language/block-client-language.component';

@NgModule({
    declarations: [
        MainComponent,
        MaintenanceComponent,
        PageHeaderComponent,
        PageFooterComponent,
        LogoComponent,
        BlockAnchorLinkComponent,
        BlockBenefitsMenuComponent,
        BlockBenefitsNewComponent,
        BlockBlockquoteComponent,
        BlockBreadcrumbsComponent,
        BlockBulletedListComponent,
        BlockButtonComponent,
        BlockCountdownComponent,
        BlockRelatedBenefitsComponent,
        BlockCategoryListComponent,
        BlockDownloadListComponent,
        BlockPersonaComponent,
        BlockHomeTileComponent,
        BlockHeroComponent,
        BlockImageTextComponent,
        BlockTextComponent,
        BlockTextEditorComponent,
        BlockHeader1Component,
        BlockHeader2Component,
        BlockHeader3Component,
        BlockHeader4Component,
        BlockHeader5Component,
        BlockNumberedListComponent,
        BlockParagraphLeadComponent,
        BlockParagraphComponent,
        BlockBackHomeComponent,
        BlockTabsComponent,
        BlockHTMLComponent,
        BlockFAQComponent,
        BlockCarouselStoriesComponent,
        BlockCardsComponent,
        BlockFeedbackComponent,
        BlockSitemapComponent,
        BlockHomeTilesComponent,
        BlockTileComponent,
        BlockToggleComponent,
        BlockDividerComponent,
        BlockFlexHeroComponent,
        BlockFlexContentComponent,
        BlockFlexStepsComponent,
        BlockFlexCardsComponent,
        BlockFlexReasontilesComponent,
        BlockFlexCarouselComponent,
        BlockFAQsComponent,
        BlockDownloadAppComponent,
        BlockVideoCustomComponent,
        BlockCardTilesComponent,
        BlockRelatedPagesComponent,
        BlockStoriesHeaderComponent,
        BlockHeadlinesIndexBannerComponent,
        BlockStoriesIndexBannerComponent,
        BlockPanelsComponent,
        BlockProviderLoginComponent,
        BlockDownloadComponent,
        BlockCloudVideoCtaComponent,
        BlockCloudReasontilesComponent,
        BlockCloudTileBanner,
        BlockCloudCTABannerComponent,
        BlockClientLanguageComponent,
    ],
    imports: [
        CommonModule,
        RouterModule,
        BsDropdownModule,
        CollapseModule,
        ModalModule,
        ButtonsModule,
        FormsModule,
        ReactiveFormsModule,
        MatProgressBarModule,
        SpinnerModule,
        TranslateModule,
        MatTabsModule,
        MatSelectModule,
        PageModule,
        MatExpansionModule,
        SwiperModule,
        MatMenuModule,
        MatIconModule,
        MatButtonModule,
        FontAwesomeModule,
        UtilsModule,
    ],
    exports: [MainComponent, CommonModule],
})
export class LayoutModule {
    constructor(private blocks: BlockService) {
        blocks.addBlock('ANCHOR-LINK', BlockAnchorLinkComponent);
        blocks.addBlock('BENEFITS-MENU', BlockBenefitsMenuComponent);
        blocks.addBlock('BENEFITS-NEW', BlockBenefitsNewComponent);
        blocks.addBlock('BLOCKQUOTE', BlockBlockquoteComponent);
        blocks.addBlock('BREADCRUMBS', BlockBreadcrumbsComponent);
        blocks.addBlock('BULLETS', BlockBulletedListComponent);
        blocks.addBlock('BUTTON', BlockButtonComponent);
        blocks.addBlock('COUNTDOWN', BlockCountdownComponent);
        blocks.addBlock('RELATED-BENEFITS', BlockRelatedBenefitsComponent);
        blocks.addBlock('CATEGORY-LIST', BlockCategoryListComponent);
        blocks.addBlock('DOWNLOAD-LIST', BlockDownloadListComponent);
        blocks.addBlock('HERO', BlockHeroComponent);
        blocks.addBlock('TILE', BlockTileComponent);
        blocks.addBlock('TOGGLE', BlockToggleComponent);
        blocks.addBlock('IMAGETEXT', BlockImageTextComponent);
        blocks.addBlock('TEXTBLOCK', BlockTextComponent);
        blocks.addBlock('TEXT-EDITOR', BlockTextEditorComponent);
        blocks.addBlock('H1', BlockHeader1Component);
        blocks.addBlock('H2', BlockHeader2Component);
        blocks.addBlock('H3', BlockHeader3Component);
        blocks.addBlock('H4', BlockHeader4Component);
        blocks.addBlock('H5', BlockHeader5Component);
        blocks.addBlock('NUMBERED-LIST', BlockNumberedListComponent);
        blocks.addBlock('PARAGRAPH-LEAD', BlockParagraphLeadComponent);
        blocks.addBlock('PARAGRAPH', BlockParagraphComponent);
        blocks.addBlock('BACKHOME', BlockBackHomeComponent);
        blocks.addBlock('TABS', BlockTabsComponent);
        blocks.addBlock('HTML', BlockHTMLComponent);
        blocks.addBlock('FAQ', BlockFAQComponent);
        blocks.addBlock('CAROUSEL-STORIES', BlockCarouselStoriesComponent);
        blocks.addBlock('CARDS', BlockCardsComponent);
        blocks.addBlock('FEEDBACK', BlockFeedbackComponent);
        blocks.addBlock('SITEMAP', BlockSitemapComponent);
        blocks.addBlock('HOME-TILES', BlockHomeTilesComponent);
        blocks.addBlock('HOME-TILE', BlockHomeTileComponent);
        blocks.addBlock('PERSONA', BlockPersonaComponent);
        blocks.addBlock('DIVIDER', BlockDividerComponent);
        blocks.addBlock('FLEX-HERO', BlockFlexHeroComponent);
        blocks.addBlock('FLEX-CONTENT', BlockFlexContentComponent);
        blocks.addBlock('FLEX-STEPS', BlockFlexStepsComponent);
        blocks.addBlock('FLEX-CLAIM', BlockFlexCardsComponent);
        blocks.addBlock('FLEX-REASONTILE', BlockFlexReasontilesComponent);
        blocks.addBlock('FLEX-CAROUSEL', BlockFlexCarouselComponent);
        blocks.addBlock('FLEX-FAQS', BlockFAQsComponent);
        blocks.addBlock('DOWNLOAD-APP', BlockDownloadAppComponent);
        blocks.addBlock('VIDEO-CUSTOM', BlockVideoCustomComponent);
        blocks.addBlock('CARD-TILES', BlockCardTilesComponent);
        blocks.addBlock('RELATED-PAGES', BlockRelatedPagesComponent);
        blocks.addBlock('ST-HEADER', BlockStoriesHeaderComponent);
        blocks.addBlock('HEAD-INDEX-BANNER', BlockHeadlinesIndexBannerComponent);
        blocks.addBlock('ST-INDEX-BANNER', BlockStoriesIndexBannerComponent);
        blocks.addBlock('PANELS', BlockPanelsComponent);
        blocks.addBlock('PROVIDER-LOGIN', BlockProviderLoginComponent);
        blocks.addBlock('DOWNLOAD', BlockDownloadComponent);
        blocks.addBlock('CLOUD-VIDEO-CTA-INTRO', BlockCloudVideoCtaComponent);
        blocks.addBlock('CLOUD-CONTENT-TILES', BlockCloudReasontilesComponent);
        blocks.addBlock('CLOUD-FW-TILE-BANNER', BlockCloudTileBanner);
        blocks.addBlock('CLOUD-CTA-BANNER', BlockCloudCTABannerComponent);
    }
}
